import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PartnerDirectoryService, PersonalDoctorOption } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { SearchFieldComponent } from '../components/common/search-field.component';
import { SpinnerComponent } from '../components/common/spinner.component';

/** Above this many, finding a name by eye stops being quicker than typing it. */
const DOCTOR_FILTERABLE = 6;

const ROW_BASE =
  'flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const ROW_OPEN = `${ROW_BASE} border-neutral-200 bg-neutral-0 hover:border-primary-300 hover:bg-primary-50/60`;
const ROW_CHOSEN = `${ROW_BASE} border-primary-600 bg-primary-50 ring-1 ring-primary-200`;

/** One row's worth of the doctor, with the parts that say nothing already dropped. */
interface DoctorRow {
  doctor: PersonalDoctorOption;
  initials: string;
  photoUrl: string;
  chosen: boolean;
  className: string;
}

function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Picks the doctor a visit is to be with, from those open to this member.
 *
 * The doctor is settled before any hour is asked about — the order the member's own app works in and
 * the order the partner's schedule insists on, since it refuses to answer without a doctor named.
 * Browsing every partner's free time and hoping the right doctor turns up in it is the wrong
 * question; the list of who may see this member is short and curated, and it is this one.
 *
 * A row shows a face and a name, and then only what tells one doctor from another. A speciality every
 * one of them shares is printed nowhere: repeated down the list it is a heading, not a distinction.
 */
@Component({
  selector: 'guarantees-doctor-picker',
  standalone: true,
  imports: [MatIcon, TranslatePipe, CalloutComponent, SearchFieldComponent, SpinnerComponent],
  templateUrl: './doctor-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorPickerComponent {
  /** The member's own user id: the directory answers with the doctors open to them. */
  userId = input.required<string>();
  selected = input<PersonalDoctorOption | undefined>(undefined);

  chosen = output<PersonalDoctorOption>();

  private readonly directory = inject(PartnerDirectoryService);

  /** Bumped to ask again after a read that failed — nothing about the question has changed. */
  private readonly attempt = signal(0);

  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  protected readonly term = signal('');

  private readonly doctors = signal<PersonalDoctorOption[]>([]);

  /** A photo the partner names but the browser cannot fetch falls back to initials, once, per row. */
  private readonly brokenPhotos = signal<string[]>([]);

  protected readonly filterable = computed(() => this.doctors().length > DOCTOR_FILTERABLE);

  /**
   * Printed only where it tells one row from another. Every family doctor is a family doctor, and a
   * column repeating that down the page is the list's heading written out once per line.
   */
  protected readonly showsSpecialization = computed(() => new Set(this.doctors().map(doctor => doctor.specialization ?? '')).size > 1);

  private readonly matched = computed(() => {
    const words = this.term().trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return this.doctors();
    }

    return this.doctors().filter(doctor => {
      const haystack = `${doctor.fullName} ${doctor.specialization ?? ''}`.toLowerCase();

      return words.every(word => haystack.includes(word));
    });
  });

  protected readonly rows = computed<DoctorRow[]>(() => {
    const chosenId = this.selected()?.id;
    const broken = this.brokenPhotos();

    return this.matched().map(doctor => {
      const chosen = doctor.id === chosenId;

      return {
        doctor,
        initials: initialsOf(doctor.fullName),
        photoUrl: doctor.photoUrl && !broken.includes(doctor.id) ? doctor.photoUrl : '',
        chosen,
        className: chosen ? ROW_CHOSEN : ROW_OPEN,
      };
    });
  });

  protected readonly empty = computed(() => !this.loading() && !this.failed() && this.doctors().length === 0);

  /** The narrowing left none, which is not the member having no doctors open to them. */
  protected readonly noMatches = computed(() => !this.loading() && !this.failed() && this.doctors().length > 0 && this.matched().length === 0);

  constructor() {
    combineLatest([toObservable(this.userId), toObservable(this.attempt)])
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
          this.doctors.set([]);
        }),
        switchMap(([userId]) =>
          this.directory.searchPersonalDoctors(userId).pipe(
            catchError(() => {
              this.failed.set(true);

              return of<PersonalDoctorOption[]>([]);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(doctors => {
        this.doctors.set(doctors);
        this.loading.set(false);
      });
  }

  protected photoFailed(doctorId: string): void {
    this.brokenPhotos.update(ids => (ids.includes(doctorId) ? ids : [...ids, doctorId]));
  }

  protected clearFilter(): void {
    this.term.set('');
  }

  protected retry(): void {
    this.attempt.update(count => count + 1);
  }
}
